/**
 * StudioCommandCenter — Operations dashboard (Premium Light Theme)
 * Combines: AI Daily Stand-up · KPIs · Setup Progress · Notification Health · AI Health · Admin Management.
 * /studio/operations
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Activity, BellRing, Bot, CheckCircle2, AlertTriangle, Shield, Users,
  Rocket, Loader2, ArrowRight, MessageSquare, Mail, Database, Sparkles,
  Sun, TrendingUp, Clock, Zap,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function StudioCommandCenter() {
  const [standup, setStandup] = useState(null);
  const [notifHealth, setNotifHealth] = useState(null);
  const [aiHealth, setAiHealth] = useState(null);
  const [progress, setProgress] = useState(null);
  const [admins, setAdmins] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, n, a, p, ad] = await Promise.allSettled([
          axios.get(`${API}/api/studio/crm/daily-standup`, { withCredentials: true }),
          axios.get(`${API}/api/studio/crm/health/notifications`, { withCredentials: true }),
          axios.get(`${API}/api/studio/crm/health/ai`, { withCredentials: true }),
          axios.get(`${API}/api/studio/crm/setup-progress`, { withCredentials: true }),
          axios.get(`${API}/api/studio/crm/admin-management`, { withCredentials: true }),
        ]);
        if (s.status === "fulfilled") setStandup(s.value.data);
        if (n.status === "fulfilled") setNotifHealth(n.value.data);
        if (a.status === "fulfilled") setAiHealth(a.value.data);
        if (p.status === "fulfilled") setProgress(p.value.data);
        if (ad.status === "fulfilled") setAdmins(ad.value.data);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div data-testid="studio-command-center" style={{ background: "#F8FAFC", minHeight: "100vh", color: "#0F172A", padding: "32px 28px" }}>
      <header style={{ maxWidth: 1400, margin: "0 auto 28px" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", background: "#EDE9FE", color: "#5B21B6", fontSize: 11, fontWeight: 700, borderRadius: 999, letterSpacing: ".08em", textTransform: "uppercase" }}>
          <Activity size={13} /> Operations Command Center
        </span>
        <h1 style={{ fontSize: 32, margin: "12px 0 6px", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.02em" }}>Studio overview</h1>
        <p style={{ color: "#64748B", margin: 0, fontSize: 15 }}>AI stand-up, health monitors, setup completion, and admin roster — all in one place.</p>
      </header>

      {loading && (
        <div style={{ maxWidth: 1400, margin: "0 auto", display: "flex", gap: 8, alignItems: "center", color: "#64748B" }}>
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}

      {!loading && (
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          {/* AI Daily Stand-up (full width) */}
          {standup && (
            <div data-testid="cc-daily-standup" style={{
              padding: 22,
              borderRadius: 14,
              background: "linear-gradient(135deg, #FFFFFF 0%, #FAF5FF 100%)",
              border: "1px solid #E9D5FF",
              boxShadow: "0 4px 14px -4px rgba(124,58,237,0.12)",
              marginBottom: 18,
              display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 24,
            }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #7C3AED, #F97316)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                    <Sun size={18} />
                  </div>
                  <div>
                    <strong style={{ fontSize: 16, color: "#0F172A" }}>AI Daily Stand-up</strong>
                    <div style={{ fontSize: 12, color: "#64748B" }}>Last 24 hours · {standup.stats.channels_scope} channel(s)</div>
                  </div>
                </div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
                  {standup.summary.map((line, i) => (
                    <li key={i} data-testid={`cc-standup-line-${i}`} style={{
                      padding: "10px 14px",
                      background: "#FFFFFF",
                      borderRadius: 10,
                      border: "1px solid #F1F5F9",
                      fontSize: 14,
                      color: "#1E293B",
                      lineHeight: 1.5,
                    }}>{line}</li>
                  ))}
                </ul>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, alignContent: "start" }}>
                <StandupStat icon={<CheckCircle2 size={14}/>} label="Published 24h" value={standup.stats.published} color="#10B981" testid="cc-standup-published"/>
                <StandupStat icon={<Clock size={14}/>} label="In review" value={standup.stats.in_review} color="#F59E0B" testid="cc-standup-review"/>
                <StandupStat icon={<AlertTriangle size={14}/>} label="Overdue" value={standup.stats.overdue} color="#EF4444" testid="cc-standup-overdue"/>
                <StandupStat icon={<Zap size={14}/>} label="AI calls today" value={standup.stats.ai_calls_today} color="#7C3AED" testid="cc-standup-ai"/>
              </div>
            </div>
          )}

          {/* Grid of health cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
            {/* Setup progress */}
            {progress && (
              <Card title="CRM Setup Progress" icon={<Rocket size={16}/>} testid="cc-setup-progress">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div style={{ flex: 1, height: 8, borderRadius: 999, background: "#F1F5F9", overflow: "hidden" }}>
                    <div style={{ width: `${progress.percent}%`, height: "100%", background: "linear-gradient(90deg, #7C3AED, #F97316)" }} />
                  </div>
                  <strong style={{ color: "#0F172A" }}>{progress.percent}%</strong>
                </div>
                <small style={{ color: "#64748B" }}>{progress.channel_title}</small>
                <ul style={{ marginTop: 12, padding: 0, listStyle: "none", display: "grid", gap: 6 }}>
                  {progress.steps.map((s) => (
                    <li key={s.key} data-testid={`cc-setup-${s.key}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                      {s.done
                        ? <CheckCircle2 size={14} color="#10B981" />
                        : <span style={{ width: 14, height: 14, borderRadius: "50%", border: "1.5px solid #CBD5E1" }} />}
                      <span style={{ color: s.done ? "#0F172A" : "#94A3B8" }}>{s.label}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Notification health */}
            {notifHealth && (
              <Card title="Notification Health" icon={<BellRing size={16}/>} testid="cc-notif-health" status={notifHealth.overall}>
                <ChannelRow label="WhatsApp (Nextel)" icon={<MessageSquare size={13}/>} ok={notifHealth.channels.whatsapp.configured} testid="cc-notif-wa"/>
                <ChannelRow label={`Email (${notifHealth.channels.email.provider})`} icon={<Mail size={13}/>} ok={notifHealth.channels.email.configured} testid="cc-notif-email"/>
                <ChannelRow label="In-app + WebSocket" icon={<Database size={13}/>} ok={notifHealth.channels.in_app.configured} testid="cc-notif-app"/>
                <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", fontSize: 12, color: "#64748B" }}>
                  <span>Sent 24h: <strong style={{ color: "#10B981" }}>{notifHealth.queue.sent_24h}</strong></span>
                  <span>Failed: <strong style={{ color: notifHealth.queue.failed_24h ? "#EF4444" : "#64748B" }}>{notifHealth.queue.failed_24h}</strong></span>
                  <span>Queued: <strong>{notifHealth.queue.pending}</strong></span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const r = await axios.post(`${API}/api/studio/crm/reminders/trigger`, {}, { withCredentials: true });
                      alert(`Reminders fired · ${JSON.stringify(r.data?.result || {})}`);
                    } catch (e) { alert(e?.response?.data?.detail || "Trigger failed"); }
                  }}
                  data-testid="cc-reminders-trigger"
                  style={{ marginTop: 12, padding: "8px 12px", fontSize: 12, width: "100%", background: "#FFFFFF", color: "#0F172A", border: "1px solid #E2E8F0", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontWeight: 600 }}
                >
                  <BellRing size={11}/> Force-trigger 9 AM reminders now
                </button>
              </Card>
            )}

            {/* AI health */}
            {aiHealth && (
              <Card title="AI Health" icon={<Bot size={16}/>} testid="cc-ai-health" status={aiHealth.overall}>
                {Object.entries(aiHealth.providers).map(([name, p]) => (
                  <ChannelRow key={name} label={`${name} · ${p.model}`} icon={<Sparkles size={13}/>} ok={p.configured} testid={`cc-ai-${name}`}/>
                ))}
                <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "#EDE9FE" }}>
                  <small style={{ color: "#5B21B6", fontWeight: 600 }}>AI-SEO usage today</small>
                  <div style={{ fontWeight: 700, color: "#0F172A", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                    {aiHealth.quota.unlimited
                      ? <><Shield size={14} color="#7C3AED"/> Unlimited (super admin)</>
                      : <>{aiHealth.quota.used_today} / {aiHealth.quota.limit} · <span style={{ color: "#10B981" }}>{aiHealth.quota.remaining} left</span></>}
                  </div>
                </div>
              </Card>
            )}

            {/* Admin management */}
            {admins && (
              <Card title="Admin Management" icon={<Shield size={16}/>} testid="cc-admin-mgmt">
                <AdminGroup label="Primary admin" rows={admins.primary_admin} accent="#F59E0B"/>
                <AdminGroup label="Secondary admins" rows={admins.secondary_admins} accent="#7C3AED"/>
                <AdminGroup label="Channel managers" rows={admins.channel_managers} accent="#3B82F6"/>
                <small style={{ color: "#64748B", fontSize: 12 }}>{admins.team_members.length} team member(s) · view all in Super Admin</small>
                <Link to="/studio/super" style={{ padding: "8px 12px", fontSize: 12, marginTop: 12, display: "inline-flex", alignItems: "center", gap: 6, background: "#FFFFFF", color: "#7C3AED", border: "1px solid #E9D5FF", borderRadius: 8, textDecoration: "none", fontWeight: 600 }} data-testid="cc-go-super">
                  Open Super Admin <ArrowRight size={11}/>
                </Link>
              </Card>
            )}

            {/* Quick links */}
            <Card title="Quick links" icon={<Sparkles size={16}/>} testid="cc-quick">
              <QuickLink to="/studio/my-tasks" label="My tasks" icon={<Users size={14}/>}/>
              <QuickLink to="/studio/quickstart" label="1-Click Quick Start" icon={<Rocket size={14}/>}/>
              <QuickLink to="/settings/integrations" label="Integrations & keys" icon={<Database size={14}/>}/>
              <QuickLink to="/tools/seo/ai" label="AI SEO toolkit" icon={<Sparkles size={14}/>}/>
              <QuickLink to="/studio/super" label="Super admin" icon={<Shield size={14}/>}/>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function StandupStat({ icon, label, value, color, testid }) {
  return (
    <div data-testid={testid} style={{ padding: 12, background: "#FFFFFF", border: "1px solid #F1F5F9", borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, color, marginBottom: 4 }}>{icon}<small style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</small></div>
      <strong style={{ fontSize: 22, color: "#0F172A", fontWeight: 700 }}>{value}</strong>
    </div>
  );
}

function Card({ title, icon, testid, status, children }) {
  const dot = status === "healthy" ? "#10B981" : status === "degraded" ? "#F59E0B" : status === "down" ? "#EF4444" : null;
  return (
    <div data-testid={testid} style={{
      padding: 20,
      borderRadius: 14,
      background: "#FFFFFF",
      border: "1px solid #E2E8F0",
      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
    }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", color: "#7C3AED" }}>
          {icon}<strong style={{ color: "#0F172A", fontSize: 14 }}>{title}</strong>
        </div>
        {dot && <span style={{ width: 9, height: 9, borderRadius: "50%", background: dot, boxShadow: `0 0 0 3px ${dot}22` }} title={status}/>}
      </header>
      {children}
    </div>
  );
}

function ChannelRow({ label, icon, ok, testid }) {
  return (
    <div data-testid={testid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
      <span style={{ display: "flex", gap: 6, alignItems: "center", color: "#475569", fontSize: 13 }}>{icon} {label}</span>
      {ok ? <CheckCircle2 size={14} color="#10B981"/> : <AlertTriangle size={14} color="#F59E0B"/>}
    </div>
  );
}

function AdminGroup({ label, rows, accent }) {
  if (!rows?.length) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <small style={{ color: accent, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontSize: 10 }}>{label}</small>
      <ul style={{ listStyle: "none", padding: 0, margin: "4px 0 0", display: "grid", gap: 4 }}>
        {rows.map((r) => (
          <li key={r.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#0F172A" }}>
            <span>{r.name}</span>
            <small style={{ color: "#94A3B8" }}>{r.last_login ? new Date(r.last_login).toLocaleDateString() : "—"}</small>
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuickLink({ to, label, icon }) {
  return (
    <Link to={to} style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "10px 12px", borderRadius: 8,
      color: "#0F172A", textDecoration: "none",
      background: "#F8FAFC", marginBottom: 6, fontSize: 13,
      border: "1px solid #F1F5F9",
      transition: "all 0.15s ease",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = "#EDE9FE"; e.currentTarget.style.borderColor = "#E9D5FF"; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "#F8FAFC"; e.currentTarget.style.borderColor = "#F1F5F9"; }}
    >
      {icon} <span style={{ flex: 1, fontWeight: 500 }}>{label}</span> <ArrowRight size={12} color="#94A3B8"/>
    </Link>
  );
}
